import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../store/AuthContext';
import { Users, Plus } from 'lucide-react';


interface StaffMember {
  id: string;
  name: string;
  role: string;
  type: 'human' | 'ai';
  notesCount: number;
  lastActive: string | null;
  email?: string;
}

const AI_AGENTS: StaffMember[] = [
  { id: 'agent-hca', name: 'HCA Agent', role: 'Health Care Assistant', type: 'ai', notesCount: 0, lastActive: null, email: 'ai@dipps.ca' },
  { id: 'agent-nurse', name: 'Nurse Agent', role: 'Registered Nurse', type: 'ai', notesCount: 0, lastActive: null, email: 'ai@dipps.ca' },
  { id: 'agent-coordinator', name: 'Coordinator Agent', role: 'Care Coordinator', type: 'ai', notesCount: 0, lastActive: null, email: 'ai@dipps.ca' },
  { id: 'agent-family', name: 'Family Agent', role: 'Family Liaison', type: 'ai', notesCount: 0, lastActive: null, email: 'ai@dipps.ca' }
];

const AI_NAME_MAP: Record<string, string[]> = {
  'agent-hca': ['Sarah M.', 'James T.', 'Linda K.', 'Robert A.', 'Maria C.'],
  'agent-nurse': ['RN Patricia H.', 'RN David L.', 'RN Susan W.'],
  'agent-coordinator': ['Care Coord. Jennifer B.', 'Care Coord. Michael S.'],
  'agent-family': []
};

export function StaffPage() {
  const { user } = useAuth();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('hca');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'human' | 'ai'>('all');

  useEffect(() => { loadStaff(); }, [user]);

  const loadStaff = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: profile } = await supabase.from('profiles').select('tenant_id').eq('id', user.id).single();
      if (!profile?.tenant_id) { setLoading(false); return; }
      setTenantId(profile.tenant_id);
      const { data: notes } = await supabase.from('care_notes').select('author_name, created_at').eq('tenant_id', profile.tenant_id).order('created_at', { ascending: false });
      const noteCounts: Record<string, number> = {};
      const lastActive: Record<string, string> = {};
      notes?.forEach(n => {
        noteCounts[n.author_name] = (noteCounts[n.author_name] || 0) + 1;
        if (!lastActive[n.author_name]) lastActive[n.author_name] = n.created_at;
      });
      const aiStaff = AI_AGENTS.map(agent => {
        const names = AI_NAME_MAP[agent.id] || [];
        const count = names.reduce((sum, name) => sum + (noteCounts[name] || 0), 0);
        const last = names.map(n => lastActive[n]).filter(Boolean).sort().reverse()[0] || null;
        return { ...agent, notesCount: count, lastActive: last };
      });
      const { data: humanProfiles } = await supabase.from('profiles').select('id, full_name, role, created_at').eq('tenant_id', profile.tenant_id).in('role', ['staff', 'admin', 'superadmin']);
      const humanStaff: StaffMember[] = (humanProfiles || []).map(p => ({
        id: p.id,
        name: p.full_name || 'Unknown',
        role: p.role === 'admin' ? 'Facility Admin' : p.role === 'superadmin' ? 'Super Admin' : 'Care Staff',
        type: 'human' as const,
        notesCount: noteCounts[p.full_name] || 0,
        lastActive: lastActive[p.full_name] || p.created_at
      }));
      setStaff([...humanStaff, ...aiStaff]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const inviteStaff = async () => {
    if (!inviteName.trim() || !inviteEmail.trim()) { setError('Please fill in name and email'); return; }
    setSaving(true); setError('');
    try {
      const { error: err } = await supabase.from('profiles').insert({
        id: crypto.randomUUID(), full_name: inviteName.trim(), role: 'staff',
        tenant_id: tenantId, language: 'en', font_size: 'medium', theme: 'light'
      });
      if (err) throw err;
      setSuccess(inviteName + ' added to staff.');
      setInviteName(''); setInviteEmail(''); setShowForm(false);
      await loadStaff();
    } catch (err: any) { setError(err.message || 'Something went wrong'); }
    finally { setSaving(false); }
  };

  const formatDate = (d: string | null) => {
    if (!d) return 'Never';
    return new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const sorted = [...staff].sort((a, b) => {
    const aTime = a.lastActive ? new Date(a.lastActive).getTime() : 0;
    const bTime = b.lastActive ? new Date(b.lastActive).getTime() : 0;
    if (bTime !== aTime) return bTime - aTime;
    if (b.notesCount !== a.notesCount) return b.notesCount - a.notesCount;
    if (a.type === 'human' && b.type === 'ai') return -1;
    if (a.type === 'ai' && b.type === 'human') return 1;
    return 0;
  });

  const filtered = sorted.filter(s => activeFilter === 'all' ? true : activeFilter === 'human' ? s.type === 'human' : s.type === 'ai');
  const humanCount = staff.filter(s => s.type === 'human').length;
  const aiCount = staff.filter(s => s.type === 'ai').length;
  const totalNotes = staff.reduce((sum, s) => sum + s.notesCount, 0);

  if (loading) return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">
      <div className="flex items-center justify-center h-[60vh] text-[rgb(var(--admin-text-muted))] text-base">Loading staff...</div>
    </main>
  );

  return (
      <main className="flex-1 p-6 space-y-6 overflow-auto">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[rgb(var(--admin-text-primary))]">Staff Management</h1>
            <p className="text-sm text-[rgb(var(--admin-text-muted))] mt-1">{humanCount} human &middot; {aiCount} AI agents &middot; {totalNotes} total notes</p>
          </div>
          <button onClick={() => setShowForm(!showForm)} className="px-5 py-3 rounded-2xl bg-[rgb(var(--admin-accent))] text-white text-sm font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity duration-200">
            <Plus className="w-4 h-4" /> Add Staff
          </button>
        </div>

        {/* Success */}
        {success && <div className="rounded-2xl p-4 bg-[rgb(var(--admin-surface-2))] border border-[rgb(var(--admin-border))] text-sm text-[rgb(var(--admin-text-secondary))]">{success}</div>}

        {/* Add Staff Form */}
        {showForm && (
          <div className="rounded-3xl p-6 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm space-y-4">
            <h3 className="text-base font-semibold text-[rgb(var(--admin-text-primary))]">Add Staff Member</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[rgb(var(--admin-text-muted))] mb-1">Full Name *</label>
                <input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="e.g. Sarah Mitchell" className="w-full px-4 py-3 rounded-2xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-sm text-[rgb(var(--admin-text-primary))]" />
              </div>
              <div>
                <label className="block text-sm text-[rgb(var(--admin-text-muted))] mb-1">Email *</label>
                <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="sarah@facility.ca" className="w-full px-4 py-3 rounded-2xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-sm text-[rgb(var(--admin-text-primary))]" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-[rgb(var(--admin-text-muted))] mb-1">Role</label>
              <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} className="w-full px-4 py-3 rounded-2xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-sm text-[rgb(var(--admin-text-primary))]">
                <option value="hca">HCA — Health Care Assistant</option>
                <option value="nurse">Nurse — Registered Nurse</option>
                <option value="coordinator">Care Coordinator</option>
                <option value="admin">Facility Admin</option>
              </select>
            </div>
            {error && <p className="text-sm text-[rgb(var(--admin-danger))]">{error}</p>}
            <div className="flex gap-3">
              <button onClick={inviteStaff} disabled={saving} className="px-5 py-3 rounded-2xl bg-[rgb(var(--admin-accent))] text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-200 disabled:opacity-50">{saving ? 'Adding...' : 'Add Staff Member'}</button>
              <button onClick={() => { setShowForm(false); setError(''); }} className="px-5 py-3 rounded-2xl bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-secondary))] text-sm font-medium border border-[rgb(var(--admin-border))]">Cancel</button>
            </div>
          </div>
        )}

        {/* System Summary */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Human Staff', value: humanCount },
            { label: 'AI Agents', value: aiCount },
            { label: 'Total Notes', value: totalNotes }
          ].map(card => (
            <div key={card.label} className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
              <p className="text-lg font-semibold text-[rgb(var(--admin-text-primary))]">{card.value}</p>
              <p className="text-sm text-[rgb(var(--admin-text-muted))]">{card.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {[
            { id: 'all' as const, label: 'All (' + staff.length + ')' },
            { id: 'human' as const, label: 'Human (' + humanCount + ')' },
            { id: 'ai' as const, label: 'AI Agents (' + aiCount + ')' }
          ].map(f => (
            <button key={f.id} onClick={() => setActiveFilter(f.id)} className={'px-4 py-2 rounded-2xl text-sm font-medium transition-colors duration-200 ' + (activeFilter === f.id ? 'bg-[rgb(var(--admin-accent)/0.15)] text-[rgb(var(--admin-accent))]' : 'bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-secondary))]')}>
              {f.label}
            </button>
          ))}
        </div>

        {/* AI Context Trust Block */}
        {(activeFilter === 'all' || activeFilter === 'ai') && (
          <div className="rounded-2xl p-4 bg-[rgb(var(--admin-surface-2))] border border-[rgb(var(--admin-border))]">
            <p className="text-sm text-[rgb(var(--admin-text-secondary))]">AI agents automatically generate care documentation to support staff. They simulate observations, wellness checks, coordination notes, and family updates to keep records consistent and up to date.</p>
          </div>
        )}

        {/* Staff List */}
        {filtered.length === 0 ? (
          <div className="rounded-3xl p-8 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm text-center">
            <p className="text-[rgb(var(--admin-text-muted))]">No staff available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(member => (
              <div key={member.id} className="rounded-3xl px-5 py-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm flex items-center justify-between hover:bg-[rgb(var(--admin-surface-2))] transition-colors duration-200 cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[rgb(var(--admin-surface-2))] flex items-center justify-center">
                    <Users className="w-5 h-5 text-[rgb(var(--admin-text-muted))]" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-[rgb(var(--admin-text-primary))]">{member.name}</p>
                    <p className="text-sm text-[rgb(var(--admin-text-muted))]">{member.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 shrink-0">
                                  <div className="w-[200px] text-right">
                    <p className="text-sm text-[rgb(var(--admin-text-secondary))]">{member.notesCount} notes</p>
                    <p className="text-xs text-[rgb(var(--admin-text-muted))]">Last active {formatDate(member.lastActive)}</p>
                  </div>
                  <div className="flex items-center gap-3 w-[80px] justify-end">
                    <span className="text-sm text-[rgb(var(--admin-text-secondary))]">{member.lastActive ? 'Active' : 'Inactive'}</span>
                    {member.type === 'ai' && <span className="text-xs text-[rgb(var(--admin-text-muted))]">AI</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </main>
  );
}